import { useState, useMemo } from 'react';
import {
  X,
  FileText,
  Clipboard,
  ClipboardCheck,
  Send,
  Download,
  Users,
  Ticket,
  Filter,
} from 'lucide-react';
import { TenantUser, Subscription, getSubscriptionCategory } from '../../types';
import { sendLicenseReport } from '../../services/api';

interface LicenseAssignmentReportProps {
  isOpen: boolean;
  onClose: () => void;
  clientId: string;
  clientName: string;
  users: TenantUser[];
  subscriptions: Subscription[];
}

type ViewMode = 'by-user' | 'by-license';

// Shorten license names for display
function shortenLicenseName(name: string): string {
  // Map raw SKU names to friendly names
  const skuNameMap: Record<string, string> = {
    'POWERAPPS_DEV': 'Power Apps (Dev)',
    'FLOW_FREE': 'Power Automate Free',
    'FLOW_P2_VIRAL': 'Power Automate (Viral)',
    'TEAMS_EXPLORATORY': 'Teams Exploratory',
    'POWER_BI_PRO': 'Power BI Pro',
    'RIGHTSMANAGEMENT_ADHOC': 'Rights Management (Ad-hoc)',
  };

  // Check for exact SKU name matches first (handles raw SKU IDs like POWERAPPS_DEV)
  if (skuNameMap[name]) return skuNameMap[name];
  if (skuNameMap[name.toUpperCase()]) return skuNameMap[name.toUpperCase()];

  return name
    .replace(/^Microsoft 365 /i, 'M365 ')
    .replace(/^Office 365 /i, 'O365 ')
    .replace(/^Enterprise Mobility \+ Security /i, 'EMS ')
    .replace(/^Azure Active Directory /i, 'AAD ')
    .replace(/^Azure AD /i, 'AAD ')
    .replace(/^Microsoft Defender for /i, 'Defender ')
    .replace(/^Microsoft /i, '')
    .replace(/^Dynamics 365 /i, 'D365 ')
    .replace(/\s*\[New Commerce Experience\]/gi, '')
    .replace(/\s*\(NCE\)/gi, '')
    .trim();
}

// Normalize product name for grouping (resolves SKU aliases so variants merge)
function normalizeForGrouping(name: string): string {
  const aliases: Record<string, string> = {
    'sharepointstorage': 'office 365 extra file storage',
    'spb': 'microsoft 365 business premium',
    'enterprisepack': 'office 365 e3',
    'enterprisepremium': 'office 365 e5',
    'exchangestandard': 'exchange online plan 1',
    'exchangeenterprise': 'exchange online plan 2',
    'exchangearchive': 'exchange online archiving',
    'flow_free': 'microsoft power automate free',
    'powerapps_viral': 'power apps free',
    'power_bi_standard': 'power bi free',
    'teams_exploratory': 'microsoft teams exploratory',
    'mcoev': 'microsoft teams phone standard',
    'intune_a': 'microsoft intune',
    'ems': 'enterprise mobility security e3',
    'emspremium': 'enterprise mobility security e5',
  };
  let n = name
    .replace(/\s*\[New Commerce Experience\]/gi, '')
    .replace(/\s*\(NCE\)/gi, '')
    .replace(/\s*NCE$/gi, '')
    .trim()
    .toLowerCase();
  const aliasKey = n.replace(/[^a-z0-9]/g, '');
  if (aliases[aliasKey]) n = aliases[aliasKey];
  const rawKey = name.trim().toLowerCase();
  if (aliases[rawKey]) n = aliases[rawKey];
  return n;
}

export default function LicenseAssignmentReport({
  isOpen,
  onClose,
  clientId,
  clientName,
  users,
  subscriptions,
}: LicenseAssignmentReportProps) {
  const [viewMode, setViewMode] = useState<ViewMode>('by-user');
  const [hideUnlicensed, setHideUnlicensed] = useState(false);
  const [copied, setCopied] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [copyFallbackText, setCopyFallbackText] = useState<string | null>(null);

  // Reset state when modal closes
  const handleClose = () => {
    setCopied(false);
    setCopyFallbackText(null);
    setRecipientEmail('');
    setEmailSent(false);
    setEmailError(null);
    onClose();
  };

  // Filter users based on hideUnlicensed toggle
  const filteredUsers = useMemo(() => {
    if (hideUnlicensed) {
      return users.filter(u => u.assignedLicenses.length > 0);
    }
    return users;
  }, [users, hideUnlicensed]);

  // Build license data grouped by normalized product name
  const licenseGroups = useMemo(() => {
    const skuIdToGroup = new Map<string, string>(); // msSkuId → normalized name
    const groups = new Map<string, {
      skuName: string;
      normalizedName: string;
      users: { displayName: string; email: string }[];
      total: number;
      assigned: number;
      isPaid: boolean;
      hasPax8: boolean;
      hasDirect: boolean;
      pax8Qty: number;
      directQty: number;
    }>();

    // Step 1: Group ALL active subscriptions by normalized product name
    // Same approach as client page: MS Direct qty = tenant truth, Pax8 is coverage only
    subscriptions.forEach(sub => {
      if (sub.status !== 'active' || sub.quantity <= 0) return;
      const normalizedName = normalizeForGrouping(sub.productName);
      const category = getSubscriptionCategory(sub);
      const isPaid = category === 'paid';

      // Map msSkuId to normalized name for user license lookup
      if (sub.msSkuId) {
        skuIdToGroup.set(sub.msSkuId, normalizedName);
      }

      const existing = groups.get(normalizedName);
      if (existing) {
        if (sub.source === 'pax8') existing.pax8Qty += sub.quantity;
        else existing.directQty += sub.quantity;
        // MS Direct qty is the tenant truth; fall back to Pax8 for Pax8-only products
        existing.total = existing.directQty > 0 ? existing.directQty : existing.pax8Qty;
        if (isPaid) existing.isPaid = true;
        if (sub.source === 'pax8') existing.hasPax8 = true;
        if (sub.source === 'direct') existing.hasDirect = true;
      } else {
        const pax8Qty = sub.source === 'pax8' ? sub.quantity : 0;
        const directQty = sub.source !== 'pax8' ? sub.quantity : 0;
        groups.set(normalizedName, {
          skuName: sub.productName,
          normalizedName,
          users: [],
          total: directQty > 0 ? directQty : pax8Qty,
          assigned: sub.assignedQuantity || 0,
          isPaid,
          hasPax8: sub.source === 'pax8',
          hasDirect: sub.source === 'direct',
          pax8Qty,
          directQty,
        });
      }
    });

    // Step 2: Add users to groups via msSkuId → normalized name lookup
    users.forEach(user => {
      user.assignedLicenses.forEach(lic => {
        let groupName = skuIdToGroup.get(lic.skuId);
        if (!groupName) {
          // Fallback: normalize the user's license skuName directly
          groupName = normalizeForGrouping(lic.skuName);
        }
        const group = groups.get(groupName);
        if (group) {
          group.users.push({
            displayName: user.displayName,
            email: user.userPrincipalName,
          });
        } else {
          // Orphaned license assignment (no matching subscription)
          groups.set(groupName, {
            skuName: lic.skuName,
            normalizedName: groupName,
            users: [{
              displayName: user.displayName,
              email: user.userPrincipalName,
            }],
            total: 0,
            assigned: 0,
            isPaid: false,
            hasPax8: false,
            hasDirect: false,
            pax8Qty: 0,
            directQty: 0,
          });
        }
      });
    });

    // Step 3: Update assigned count from actual user data (more accurate)
    groups.forEach(group => {
      if (group.users.length > 0) {
        group.assigned = group.users.length;
      }
    });

    // Step 4: Filter out free/trial groups with no users assigned
    const result = Array.from(groups.values()).filter(g => {
      if (!g.isPaid && g.users.length === 0) return false;
      return true;
    });

    // Step 5: Sort — paid by total desc, paid unassigned-only at bottom of paid, free at end
    result.sort((a, b) => {
      if (a.isPaid && !b.isPaid) return -1;
      if (!a.isPaid && b.isPaid) return 1;
      if (a.isPaid && b.isPaid) {
        const aHasUsers = a.users.length > 0;
        const bHasUsers = b.users.length > 0;
        if (aHasUsers && !bHasUsers) return -1;
        if (!aHasUsers && bHasUsers) return 1;
        return b.total - a.total;
      }
      return a.skuName.localeCompare(b.skuName);
    });

    return result;
  }, [users, subscriptions]);


  // Total unused licenses across paid groups only
  const totalUnused = useMemo(() => {
    return licenseGroups.reduce((sum, g) => {
      if (g.isPaid) return sum + Math.max(0, g.total - g.assigned);
      return sum;
    }, 0);
  }, [licenseGroups]);

  // Summary stats (always from full user list for accuracy)
  const stats = useMemo(() => {
    const usersWithLicenses = users.filter(u => u.assignedLicenses.length > 0).length;
    const usersWithoutLicenses = users.filter(u => u.assignedLicenses.length === 0).length;
    const totalAssignments = users.reduce((sum, u) => sum + u.assignedLicenses.length, 0);
    return { usersWithLicenses, usersWithoutLicenses, totalAssignments };
  }, [users]);

  // Generate report text for copy/email
  const generateReportText = (mode: ViewMode, includeUnlicensed: boolean = true): string => {
    const timestamp = new Date().toLocaleString();
    const lines: string[] = [
      `License Assignment Report - ${clientName}`,
      `Generated: ${timestamp}`,
      ``,
    ];

    const usersToInclude = includeUnlicensed
      ? users
      : users.filter(u => u.assignedLicenses.length > 0);

    if (mode === 'by-user') {
      lines.push(`${'User'.padEnd(30)} ${'Email'.padEnd(40)} Licenses`);
      lines.push('─'.repeat(100));

      // Sort users alphabetically
      const sortedUsers = [...usersToInclude].sort((a, b) =>
        a.displayName.localeCompare(b.displayName)
      );

      sortedUsers.forEach(user => {
        const licenses = user.assignedLicenses.length > 0
          ? user.assignedLicenses.map(l => shortenLicenseName(l.skuName)).join(', ')
          : '(No licenses)';
        lines.push(`${user.displayName.substring(0, 30).padEnd(30)} ${user.userPrincipalName.substring(0, 40).padEnd(40)} ${licenses}`);
      });

      lines.push('');
      lines.push(`Summary: ${stats.usersWithLicenses} users with licenses, ${stats.usersWithoutLicenses} without, ${stats.totalAssignments} total assignments`);
    } else {
      // By License view
      licenseGroups.forEach(group => {
        const unused = group.isPaid ? group.total - group.assigned : 0;
        let availability: string;
        if (group.isPaid) {
          availability = `(${group.assigned} assigned / ${group.total} total${unused > 0 ? ` — ${unused} UNUSED` : ''})`;
        } else {
          availability = `(${group.assigned} assigned) [Free]`;
        }
        const migration = (group.hasDirect && !group.hasPax8 && group.isPaid) ? ' [MS Direct - Migrate to Pax8]' : '';
        lines.push(`${shortenLicenseName(group.skuName)} ${availability}${migration}`);
        lines.push('─'.repeat(60));

        if (group.users.length === 0) {
          lines.push('  (No users assigned)');
        } else {
          group.users.sort((a, b) => a.displayName.localeCompare(b.displayName));
          group.users.forEach(user => {
            lines.push(`  • ${user.displayName} (${user.email})`);
          });
        }
        lines.push('');
      });

      lines.push(`Summary: ${licenseGroups.length} license types, ${stats.totalAssignments} total assignments, ${totalUnused} unused licenses`);
    }

    return lines.join('\n');
  };

  // Generate CSV content
  const generateCSV = (): string => {
    const usersToInclude = hideUnlicensed
      ? users.filter(u => u.assignedLicenses.length > 0)
      : users;

    const rows: string[][] = [['User', 'Email', 'Job Title', 'Licenses']];

    const sortedUsers = [...usersToInclude].sort((a, b) =>
      a.displayName.localeCompare(b.displayName)
    );

    sortedUsers.forEach(user => {
      const licenses = user.assignedLicenses.map(l => l.skuName).join('; ');
      rows.push([
        user.displayName,
        user.userPrincipalName,
        user.jobTitle || '',
        licenses || '(None)',
      ]);
    });

    // Escape and format CSV
    return rows.map(row =>
      row.map(cell => {
        if (cell.includes(',') || cell.includes('"') || cell.includes('\n')) {
          return `"${cell.replace(/"/g, '""')}"`;
        }
        return cell;
      }).join(',')
    ).join('\n');
  };

  // Generate structured data for email (not text-based)
  const generateEmailData = () => {
    const licensedUsers = users.filter(u => u.assignedLicenses.length > 0);
    return {
      users: licensedUsers.map(u => ({
        displayName: u.displayName,
        email: u.userPrincipalName,
        jobTitle: u.jobTitle || '',
        licenses: u.assignedLicenses.map(l => shortenLicenseName(l.skuName)),
      })).sort((a, b) => a.displayName.localeCompare(b.displayName)),
      licenseGroups: licenseGroups.map(g => ({
        name: shortenLicenseName(g.skuName),
        assigned: g.assigned,
        total: g.total,
        unused: g.isPaid ? Math.max(0, g.total - g.assigned) : 0,
        isPaid: g.isPaid,
        needsMigration: g.hasDirect && !g.hasPax8 && g.isPaid,
        users: g.users.map(u => ({ displayName: u.displayName, email: u.email }))
          .sort((a, b) => a.displayName.localeCompare(b.displayName)),
      })),
    };
  };

  // Handle copy to clipboard with fallback for iframe context
  const handleCopy = async () => {
    const textToCopy = generateReportText(viewMode, !hideUnlicensed);

    try {
      // Try modern clipboard API first
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(textToCopy);
        setCopied(true);
        setTimeout(() => setCopied(false), 3000);
        return;
      }

      // Fallback for iframe or non-secure context
      const textArea = document.createElement('textarea');
      textArea.value = textToCopy;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);

      if (successful) {
        setCopied(true);
        setTimeout(() => setCopied(false), 3000);
      } else {
        // Show inline textarea so user can Ctrl+A, Ctrl+C manually
        setCopyFallbackText(textToCopy);
      }
    } catch (err) {
      console.error('Failed to copy:', err);
      // Show inline textarea so user can Ctrl+A, Ctrl+C manually
      setCopyFallbackText(textToCopy);
    }
  };

  // Handle CSV download
  const handleDownloadCSV = () => {
    const csv = generateCSV();
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `license-report-${clientName.replace(/[^a-z0-9]/gi, '-')}-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Handle send email
  const handleSendEmail = async () => {
    if (!recipientEmail) return;

    setSendingEmail(true);
    setEmailError(null);

    try {
      const emailData = generateEmailData();
      const result = await sendLicenseReport({
        recipientEmail,
        clientId,
        clientName,
        reportText: generateReportText('by-user', false), // Only licensed users for email
        reportByLicense: generateReportText('by-license'),
        stats: {
          totalUsers: users.length,
          usersWithLicenses: stats.usersWithLicenses,
          usersWithoutLicenses: stats.usersWithoutLicenses,
          totalAssignments: stats.totalAssignments,
          licenseTypes: licenseGroups.length,
          unusedLicenses: totalUnused,
        },
        // Send structured data for professional email rendering
        userData: emailData.users,
        licenseData: emailData.licenseGroups,
      });

      if (result.success) {
        setEmailSent(true);
      } else {
        setEmailError(result.error || 'Failed to send email');
      }
    } catch (err) {
      console.error('Failed to send email:', err);
      setEmailError(err instanceof Error ? err.message : 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 dark:bg-gray-900 opacity-75" onClick={handleClose}></div>
        </div>

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-card rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full relative">
          {/* Toast Notification */}
          {copied && (
            <div className="absolute top-0 left-0 right-0 z-10 animate-pulse">
              <div className="bg-green-500 text-white px-4 py-3 flex items-center justify-center shadow-lg">
                <ClipboardCheck className="h-5 w-5 mr-2" />
                <span className="font-medium">Copied to clipboard!</span>
              </div>
            </div>
          )}

          {/* Header */}
          <div className="px-6 py-4 border-b border-border bg-gradient-to-r from-primary-600 to-primary-700 flex justify-between items-center">
            <div className="flex items-center">
              <FileText className="h-6 w-6 text-white mr-2" />
              <div>
                <h3 className="text-lg font-semibold text-white">License Assignment Report</h3>
                <p className="text-sm text-white/80">{clientName}</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-2 text-white/80 hover:text-white"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* View Toggle & Filter */}
          <div className="px-6 py-3 bg-muted border-b border-border flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center space-x-4">
              {/* View Toggle */}
              <div className="flex items-center space-x-2">
                <span className="text-sm text-muted-foreground">View:</span>
                <div className="flex bg-card rounded-lg border border-border p-1">
                  <button
                    onClick={() => setViewMode('by-user')}
                    className={`px-3 py-1.5 text-sm font-medium rounded-md flex items-center transition-colors ${
                      viewMode === 'by-user'
                        ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <Users className="h-4 w-4 mr-1.5" />
                    By User
                  </button>
                  <button
                    onClick={() => setViewMode('by-license')}
                    className={`px-3 py-1.5 text-sm font-medium rounded-md flex items-center transition-colors ${
                      viewMode === 'by-license'
                        ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <Ticket className="h-4 w-4 mr-1.5" />
                    By License
                  </button>
                </div>
              </div>

              {/* Hide Unlicensed Toggle (By User) */}
              {viewMode === 'by-user' && (
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={hideUnlicensed}
                    onChange={(e) => setHideUnlicensed(e.target.checked)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-border rounded"
                  />
                  <span className="text-sm text-muted-foreground flex items-center">
                    <Filter className="h-4 w-4 mr-1" />
                    Hide unlicensed users
                  </span>
                </label>
              )}

            </div>

            {/* Stats */}
            <div className="flex items-center space-x-4 text-sm">
              <span className="text-muted-foreground">
                <span className="font-medium text-foreground">{filteredUsers.length}</span>
                {hideUnlicensed ? ' licensed' : ''} users
              </span>
              <span className="text-muted-foreground">
                <span className="font-medium text-foreground">{stats.totalAssignments}</span> assignments
              </span>
              <span className="text-muted-foreground">
                <span className="font-medium text-foreground">{licenseGroups.length}</span> license types
              </span>
              {totalUnused > 0 && (
                <span className="text-orange-600 dark:text-orange-400 font-medium">
                  {totalUnused} unused
                </span>
              )}
            </div>
          </div>

          {/* Report Content */}
          <div className="px-6 py-4 max-h-[50vh] overflow-y-auto">
            {viewMode === 'by-user' ? (
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-2 px-2 font-medium text-muted-foreground">User</th>
                    <th className="text-left py-2 px-2 font-medium text-muted-foreground">Email</th>
                    <th className="text-left py-2 px-2 font-medium text-muted-foreground">Licenses</th>
                  </tr>
                </thead>
                <tbody>
                  {[...filteredUsers]
                    .sort((a, b) => a.displayName.localeCompare(b.displayName))
                    .map(user => (
                      <tr key={user.id} className="border-b border-border hover:bg-accent">
                        <td className="py-2 px-2 text-foreground">{user.displayName}</td>
                        <td className="py-2 px-2 text-muted-foreground font-mono text-xs">{user.userPrincipalName}</td>
                        <td className="py-2 px-2">
                          {user.assignedLicenses.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {user.assignedLicenses.map(lic => (
                                <span
                                  key={lic.skuId}
                                  className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300"
                                >
                                  {shortenLicenseName(lic.skuName)}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-muted-foreground italic">No licenses</span>
                          )}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            ) : (
              <div className="space-y-6">
                {licenseGroups.map(group => {
                  const unused = group.isPaid ? group.total - group.assigned : 0;
                  const needsMigration = group.hasDirect && !group.hasPax8 && group.isPaid;
                  const borderColor = needsMigration
                    ? 'border-red-300 dark:border-red-700'
                    : unused > 0
                      ? 'border-orange-300 dark:border-orange-700'
                      : 'border-border';
                  const headerBg = needsMigration
                    ? 'bg-red-50 dark:bg-red-900/20'
                    : unused > 0
                      ? 'bg-orange-50 dark:bg-orange-900/20'
                      : 'bg-muted';
                  return (
                    <div key={group.normalizedName} className={`border rounded-lg overflow-hidden ${borderColor}`}>
                      <div className={`px-4 py-3 flex items-center justify-between ${headerBg}`}>
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium text-foreground">
                            {shortenLicenseName(group.skuName)}
                          </h4>
                          {!group.isPaid && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground">
                              Free
                            </span>
                          )}
                          {needsMigration && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300">
                              MS Direct — Migrate to Pax8
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300">
                            {group.assigned} assigned
                          </span>
                          {group.isPaid && (
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${unused > 0 ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-300' : 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300'}`}>
                              {unused} unassigned
                            </span>
                          )}
                          {group.isPaid && (
                            <span className="text-xs text-muted-foreground">
                              of {group.total}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="px-4 py-3">
                        {group.users.length === 0 ? (
                          <p className="text-muted-foreground italic text-sm">No users assigned</p>
                        ) : (
                          <ul className="space-y-1">
                            {group.users
                              .sort((a, b) => a.displayName.localeCompare(b.displayName))
                              .map((user, idx) => (
                                <li key={idx} className="text-sm flex items-center">
                                  <span className="text-muted-foreground mr-2">•</span>
                                  <span className="text-foreground">{user.displayName}</span>
                                  <span className="text-muted-foreground ml-2 font-mono text-xs">({user.email})</span>
                                </li>
                              ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="px-6 py-4 border-t border-border bg-muted space-y-4">
            {/* Action Buttons Row */}
            <div className="flex items-center gap-3">
              {/* Copy Button */}
              <button
                onClick={handleCopy}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                  copied
                    ? 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700 text-green-700 dark:text-green-300'
                    : 'bg-card border-border text-muted-foreground hover:bg-accent'
                }`}
              >
                {copied ? (
                  <ClipboardCheck className="h-5 w-5" />
                ) : (
                  <Clipboard className="h-5 w-5" />
                )}
                {copied ? 'Copied!' : 'Copy to Clipboard'}
              </button>

              {/* Download CSV Button */}
              <button
                onClick={handleDownloadCSV}
                className="flex items-center gap-2 px-4 py-2 rounded-lg border bg-card border-border text-muted-foreground hover:bg-accent transition-colors"
              >
                <Download className="h-5 w-5" />
                Download CSV
              </button>
            </div>

            {/* Clipboard fallback for iframe mode */}
            {copyFallbackText && (
              <div className="rounded-lg border border-blue-200 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20 p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-blue-800 dark:text-blue-300">Select all text below and press Ctrl+C to copy</span>
                  <button onClick={() => setCopyFallbackText(null)} className="text-blue-600 dark:text-blue-400 text-xs hover:underline">Dismiss</button>
                </div>
                <textarea
                  readOnly
                  value={copyFallbackText}
                  onFocus={(e) => e.target.select()}
                  className="w-full h-40 text-xs font-mono bg-card border border-border rounded p-2 text-foreground"
                />
              </div>
            )}

            {/* Email Section */}
            <div className="rounded-lg border border-border bg-card overflow-hidden">
              <div className="flex items-center gap-3 px-4 py-3 border-b border-border">
                <Send className="h-5 w-5 text-muted-foreground" />
                <div className="text-left flex-1">
                  <div className="font-medium text-muted-foreground">Email Report</div>
                  <div className="text-xs text-muted-foreground">Send professional formatted report (licensed users only)</div>
                </div>
              </div>

              {emailSent ? (
                <div className="px-4 py-3 bg-green-50 dark:bg-green-900/20">
                  <div className="flex items-center gap-2 text-green-700 dark:text-green-300">
                    <ClipboardCheck className="h-5 w-5" />
                    <span className="font-medium">Email sent to {recipientEmail}</span>
                  </div>
                </div>
              ) : (
                <div className="px-4 py-3 flex items-center gap-3">
                  <input
                    type="email"
                    value={recipientEmail}
                    onChange={(e) => setRecipientEmail(e.target.value)}
                    placeholder="recipient@example.com"
                    className="flex-1 px-3 py-2 text-sm border border-border rounded-lg bg-card text-foreground placeholder-muted-foreground focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <button
                    onClick={handleSendEmail}
                    disabled={!recipientEmail || sendingEmail}
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {sendingEmail ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4" />
                        Send
                      </>
                    )}
                  </button>
                </div>
              )}
              {emailError && (
                <div className="px-4 py-2 bg-red-50 dark:bg-red-900/20 text-sm text-red-600 dark:text-red-400">
                  {emailError}
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-3 border-t border-border flex justify-end">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-muted-foreground bg-card border border-border rounded-lg hover:bg-accent"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
